import React, { useEffect, useState } from 'react';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useDispatch } from 'react-redux';
import { handleGetRequest } from '../../service/GetTemplate';
import { handlePostRequest } from '../../service/PostTemplate';
import { handlePutRequest } from '../../service/PutTemplate';
import { endPoints } from '../../Config';
import { InputTextarea } from 'primereact/inputtextarea';
import { getPreferredLanguage } from '../../utilities/languagePreference';
import { translateUiText } from '../../utilities/siteTranslations';

const conditionOptions = [
    { label: 'New', value: 'New' },
    { label: 'Ready to Install', value: 'Ready to Install' },
    { label: 'Needs Repair', value: 'Needs Repair' },
    { label: 'Used', value: 'Used' },
    { label: 'Damaged', value: 'Damaged' }
];

const categoryOptions = [
    { label: 'Electrical Coffee', value: 'Electrical Coffee' },
    { label: 'Electrical Snack', value: 'Electrical Snack' },
    { label: 'Exterior Coffee', value: 'Exterior Coffee' },
    { label: 'Exterior Snack', value: 'Exterior Snack' },
    { label: 'Hydraulics Coffee', value: 'Hydraulics Coffee' },
    { label: 'Hydraulics Snack', value: 'Hydraulics Snack' },
    { label: 'Mechanical Coffee', value: 'Mechanical Coffee' },
    { label: 'Mechanical Snack', value: 'Mechanical Snack' }
];

const typeList = [
    {
        id: 'snack',
        name: 'Snacks'
    },
    {
        id: 'coffee',
        name: 'Coffee'
    },
    {
        id: 'combo',
        name: 'Combo'
    },
    {
        id: 'drink',
        name: 'Drink'
    },
    {
        id: 'others',
        name: 'Others'
    }
];

const roundToTwo = (value) => Math.round((value + Number.EPSILON) * 100) / 100;
const isLegacyAuditFieldError = (response) => {
    const message = response?.data?.messages || response?.data?.message || '';
    return typeof message === 'string' && (message.includes('performedBy is not allowed') || message.includes('auditComment is not allowed'));
};

const AddEditPartsInventory = ({ editable, viewable, onHide, rowRecord, onClose, preferredLanguage: preferredLanguageProp }) => {
    const [loadingBtn, setLoadingBtn] = useState(false);
    const [allUsers, setAllUsers] = useState([]);
    const [preferredLanguage, setPreferredLanguage] = useState(preferredLanguageProp || getPreferredLanguage());
    const dispatch = useDispatch();
    const currentUser = JSON.parse(localStorage.getItem('userDetails') || '{}');
    const currentAdmin = currentUser?.data?.adminUser || {};
    const t = (value) => translateUiText(value, preferredLanguage);
    const localizedConditionOptions = conditionOptions.map((option) => ({ ...option, label: t(option.label) }));
    const localizedCategoryOptions = categoryOptions.map((option) => ({ ...option, label: t(option.label) }));
    const localizedTypeList = typeList.map((option) => ({ ...option, name: t(option.name) }));

    const validationSchema = Yup.object().shape({
        orderNumber: Yup.string()
            .matches(/^[^\s]+(\s[^\s]+)*$/, t('Leading and trailing space is not allowed'))
            .required(t('Order Number is required'))
            .matches(/^[a-zA-Z0-9\-]*$/, t('Order Number must contain only alphanumeric values and hyphens')),
        orderDate: Yup.date().required(t('Order Date is required')),
        sku: Yup.string()
            .matches(/^[^\s]+(\s[^\s]+)*$/, t('Leading and trailing space is not allowed'))
            .required(t('Part Code/SKU is required'))
            .matches(/^[a-zA-Z0-9\-]*$/, t('Part Code must contain only alphanumeric values and hyphens')),
        quantityInStock: Yup.number()
            .required(t('Quantity is required'))
            .min(1, t('Quantity must be at least 1'))
            .integer(t('Quantity must be a whole number')),
        orderedBy: Yup.mixed().required(t('Ordered By is required')),
        condition: Yup.mixed().required(t('Condition is required')),
        category: Yup.mixed().required(t('Category is required')),
        location: Yup.mixed().required(t('Location is required')),
        pricePerUnit: Yup.number()
            .typeError(t('Price Per Unit must be a valid number'))
            .required(t('Price Per Unit is required'))
            .min(0, t('Price Per Unit cannot be negative')),
        pricePerTotal: Yup.number()
            .typeError(t('Price Per Total must be a valid number'))
            .required(t('Price Per Total is required'))
            .min(0, t('Price Per Total cannot be negative')),
        description: Yup.string()
            .transform((value) => (typeof value === 'string' ? value.trim() : value))
            .required(t('Description is required'))
            .min(2, t('Description is too short'))
            .max(200, t('Description must be at most 200 characters')),
        machineType: Yup.mixed().required(t('Machine Type is required'))
    });

    const formik = useFormik({
        validationSchema,
        initialValues: {
            id: '',
            orderNumber: '',
            orderDate: '',
            orderedBy: '',
            sku: '',
            description: '',
            quantityInStock: 0,
            location: '',
            condition: '',
            pricePerUnit: 0,
            pricePerTotal: 0,
            category: '',
            machineType: '',
            auditComment: ''
        },
        onSubmit: async (data) => {
            setLoadingBtn(true);

            const quantity = Number(data?.quantityInStock || 0);
            const unitPrice = Number(data?.pricePerUnit || 0);
            const computedTotal = roundToTwo(quantity * unitPrice);

            const addData = {
                orderNumber: data?.orderNumber,
                orderDate: data?.orderDate ? new Date(data.orderDate).toISOString().split('T')[0] : null,
                orderedBy: data?.orderedBy,
                sku: data?.sku,
                description: data?.description,
                quantityInStock: data?.quantityInStock,
                location: data?.location,
                condition: data?.condition,
                pricePerUnit: data?.pricePerUnit,
                pricePerTotal: computedTotal,
                category: data?.category,
                machineType: data?.machineType,
                performedBy: currentAdmin?._id || '',
                auditComment: data?.auditComment || ''
            };
           

            if (!editable) {
                let response = await dispatch(handlePostRequest(addData, endPoints.addPartsInventory, true));
                if (isLegacyAuditFieldError(response)) {
                    const { performedBy, auditComment, ...legacyAddData } = addData;
                    response = await dispatch(handlePostRequest(legacyAddData, endPoints.addPartsInventory, true));
                }
                if (response?.status === 201) {
                    formik.resetForm();
                    onHide();
                }
                setLoadingBtn(false);
            } else {
                const id = data?.id ;
                const editData = {
                    ...addData,
                    id: id,
                };
                let response = await dispatch(handlePutRequest(editData, endPoints.updatePartsInventory, true));
                if (isLegacyAuditFieldError(response)) {
                    const { performedBy, auditComment, ...legacyEditData } = editData;
                    response = await dispatch(handlePutRequest(legacyEditData, endPoints.updatePartsInventory, true));
                }
                if (response?.status === 200) {
                    formik.resetForm();
                    onHide();
                }
                setLoadingBtn(false);
            }
        }
    });

    const getPartsInventoryById = async () => {
        setLoadingBtn(true);
        // Uncomment when API is ready
        const response = await handleGetRequest(endPoints.getPartsInventoryById(rowRecord._id), true);
        if (response) {
            formik.setFieldValue('id', response?.data?._id);
            formik.setFieldValue('orderNumber', response?.data?.orderNumber);
            formik.setFieldValue('orderDate', new Date(response?.data?.orderDate));
            formik.setFieldValue('sku', response?.data?.sku);
            formik.setFieldValue('quantityInStock', response?.data?.quantityInStock);
            formik.setFieldValue('orderedBy', response?.data?.orderedBy?._id);
            formik.setFieldValue('condition', response?.data?.condition);
            formik.setFieldValue('category', response?.data?.category);
            formik.setFieldValue('location', response?.data?.location);
            formik.setFieldValue('pricePerUnit', response?.data?.pricePerUnit);
            formik.setFieldValue('pricePerTotal', response?.data?.pricePerTotal);
            formik.setFieldValue('description', response?.data?.description);
            formik.setFieldValue('machineType', response?.data?.machineType);   
        }

       
        setLoadingBtn(false);
    };

    const getUserList = async () => {
        const response = await handleGetRequest(endPoints.getallUsers, true, false, dispatch);
        if (response) {
            setAllUsers(response?.data);
        }
    };
    useEffect(() => {
        getUserList();
    }, []);

    useEffect(() => {
        setPreferredLanguage(preferredLanguageProp || getPreferredLanguage());
    }, [preferredLanguageProp]);

    useEffect(() => {
        if (editable || viewable) {
            getPartsInventoryById();
        }
    }, [rowRecord]);

    useEffect(() => {
        const quantity = Number(formik.values.quantityInStock || 0);
        const unitPrice = Number(formik.values.pricePerUnit || 0);
        const computedTotal = roundToTwo(quantity * unitPrice);
        const currentTotal = Number(formik.values.pricePerTotal || 0);

        if (currentTotal !== computedTotal) {
            formik.setFieldValue('pricePerTotal', computedTotal, false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formik.values.quantityInStock, formik.values.pricePerUnit]);

    return (
        <form onSubmit={formik.handleSubmit}>
            <div className="grid p-fluid">
                <div className="col-12 md:col-6">
                    <div className="field mb-3">
                        <label htmlFor="orderNumber">
                            {t('Order Number')} <span style={{ color: 'red' }}>*</span>
                        </label>
                        <InputText id="orderNumber" name="orderNumber" placeholder={t('Enter Order Number (e.g., ORD-2024-001)')} value={formik?.values?.orderNumber} maxLength={50} onChange={formik.handleChange} disabled={viewable} />
                        {formik.touched.orderNumber && formik.errors.orderNumber && <small className="p-error">{formik.errors.orderNumber}</small>}
                    </div>
                </div>
                {!viewable && (
                    <div className="col-12">
                        <div className="field mb-3">
                            <label htmlFor="auditComment">{editable ? t('Inventory Change Comment') : t('Inventory Activity Comment')}</label>
                            <InputTextarea
                                id="auditComment"
                                name="auditComment"
                                rows={3}
                                value={formik?.values?.auditComment}
                                onChange={formik.handleChange}
                                placeholder={editable ? t('Optional reason for this stock or inventory update') : t('Optional note for this new inventory item')}
                            />
                        </div>
                    </div>
                )}
                <div className="col-12 md:col-6">
                    <div className="field mb-3">
                        <label htmlFor="orderDate">
                            {t('Order Date')} <span style={{ color: 'red' }}>*</span>
                        </label>
                        <Calendar
                            id="orderDate"
                            name="orderDate"
                            value={formik?.values?.orderDate}
                            onChange={(e) => formik.setFieldValue('orderDate', e.value)}
                            dateFormat="yy-mm-dd"
                            placeholder={t('Select Order Date')}
                            disabled={viewable}
                            className="w-full"
                            showIcon
                        />
                        {formik.touched.orderDate && formik.errors.orderDate && <small className="p-error">{formik.errors.orderDate}</small>}
                    </div>
                </div>
                <div className="col-12 md:col-6">
                    <div className="field mb-3">
                        <label htmlFor="sku">
                            {t('Part Code/SKU')} <span style={{ color: 'red' }}>*</span>
                        </label>
                        <InputText id="sku" name="sku" placeholder={t('Enter Part Code or SKU (e.g., SKU-ENG-001)')} value={formik?.values?.sku} maxLength={50} onChange={formik.handleChange} disabled={viewable} />
                        {formik.touched.sku && formik.errors.sku && <small className="p-error">{formik.errors.sku}</small>}
                    </div>
                </div>
                <div className="col-12 md:col-6">
                    <div className="field mb-3">
                        <label htmlFor="quantityInStock">
                            {t('Quantity')} <span style={{ color: 'red' }}>*</span>
                        </label>
                        <InputNumber
                            id="quantityInStock"
                            name="quantityInStock"
                            value={formik?.values?.quantityInStock}
                            onValueChange={(e) => formik.setFieldValue('quantityInStock', e.value)}
                            placeholder={t('Quantity')}
                            min={0}
                            max={999999}
                            disabled={viewable}
                            className="w-full"
                        />
                        {formik.touched.quantityInStock && formik.errors.quantityInStock && <small className="p-error">{formik.errors.quantityInStock}</small>}
                    </div>
                </div>
                <div className="col-12 md:col-6">
                    <div className="field mb-3">
                        <label htmlFor="orderedBy">
                            {t('Ordered By')} <span style={{ color: 'red' }}>*</span>
                        </label>
                        <Dropdown
                            id="orderedBy"
                            name="orderedBy"
                            value={formik?.values?.orderedBy}
                            options={allUsers}
                            optionLabel="firstName"
                            optionValue="_id"
                            onChange={(e) => formik.setFieldValue('orderedBy', e.value)}
                            placeholder={t('Select User')}
                            className="w-full"
                            disabled={viewable}
                        />
                        {formik.touched.orderedBy && formik.errors.orderedBy && <small className="p-error">{formik.errors.orderedBy}</small>}
                    </div>
                </div>
                <div className="col-12 md:col-6">
                    <div className="field mb-3">
                        <label htmlFor="condition">
                            {t('Condition')} <span style={{ color: 'red' }}>*</span>
                        </label>
                        <Dropdown
                            id="condition"
                            name="condition"
                            value={formik?.values?.condition}
                            options={localizedConditionOptions}
                            optionLabel="label"
                            optionValue="value"
                            onChange={(e) => formik.setFieldValue('condition', e.value)}
                            placeholder={t('Select Condition')}
                            className="w-full"
                            disabled={viewable}
                        />
                        {formik.touched.condition && formik.errors.condition && <small className="p-error">{formik.errors.condition}</small>}
                    </div>
                </div>
                <div className="col-12 md:col-6">
                    <div className="field mb-3">
                        <label htmlFor="category">
                            {t('Category')} <span style={{ color: 'red' }}>*</span>
                        </label>
                        <Dropdown
                            id="category"
                            name="category"
                            value={formik?.values?.category}
                            options={localizedCategoryOptions}
                            optionLabel="label"
                            optionValue="value"
                            onChange={(e) => formik.setFieldValue('category', e.value)}
                            placeholder={t('Select Category')}
                            className="w-full"
                            disabled={viewable}
                        />
                        {formik.touched.category && formik.errors.category && <small className="p-error">{formik.errors.category}</small>}
                    </div>
                </div>
                <div className="col-12 md:col-6">
                    <div className="field mb-3">
                        <label htmlFor="machineType">
                            {t('Machine Type')} <span style={{ color: 'red' }}>*</span>
                        </label>
                        <Dropdown
                            id="machineType"
                            name="machineType"
                            value={formik?.values?.machineType}
                            options={localizedTypeList}
                            optionLabel="name"
                            optionValue="id"
                            onChange={(e) => formik.setFieldValue('machineType', e.value)}
                            placeholder={t('Select machineType')}
                            className="w-full"
                            disabled={viewable}
                        />
                        {formik.touched.machineType && formik.errors.machineType && <small className="p-error">{formik.errors.machineType}</small>}
                    </div>
                </div>
                <div className="col-12 md:col-6">
                    <div className="field mb-3">
                        <label htmlFor="location">
                            {t('Location')} <span style={{ color: 'red' }}>*</span>
                        </label>
                        <InputText id="location" name="location" placeholder={t('Enter Location')} value={formik?.values?.location} maxLength={50} onChange={formik.handleChange} disabled={viewable} />

                        {formik.touched.location && formik.errors.location && <small className="p-error">{formik.errors.location}</small>}
                    </div>
                </div>
                <div className="col-12 md:col-6">
                    <div className="field mb-3">
                        <label htmlFor="pricePerUnit">
                            {t('Price Per Unit')} <span style={{ color: 'red' }}>*</span>
                        </label>
                        <InputNumber
                            id="pricePerUnit"
                            name="pricePerUnit"
                            value={formik?.values?.pricePerUnit}
                            onValueChange={(e) => formik.setFieldValue('pricePerUnit', e.value)}
                            placeholder="$0.00"
                            mode="decimal"
                            min={0}
                            minFractionDigits={0}
                            maxFractionDigits={2}
                            disabled={viewable}
                            className="w-full"
                        />
                        {formik.touched.pricePerUnit && formik.errors.pricePerUnit && <small className="p-error">{formik.errors.pricePerUnit}</small>}
                    </div>
                </div>
                <div className="col-12 md:col-6">
                    <div className="field mb-3">
                        <label htmlFor="pricePerTotal">
                            {t('Price Per Total')} <span style={{ color: 'red' }}>*</span>
                        </label>
                        <InputNumber
                            id="pricePerTotal"
                            name="pricePerTotal"
                            value={formik?.values?.pricePerTotal}
                            placeholder="$0.00"
                            mode="decimal"
                            min={0}
                            minFractionDigits={0}
                            maxFractionDigits={2}
                            inputProps={{ readOnly: true }}
                            disabled={viewable}
                            className="w-full"
                        />
                        {formik.touched.pricePerTotal && formik.errors.pricePerTotal && <small className="p-error">{formik.errors.pricePerTotal}</small>}
                    </div>
                </div>{' '}
                <div className="col-12 md:col-12">
                    <div className="field mb-3">
                        <label htmlFor="description">
                            {t('Description')} <span style={{ color: 'red' }}>*</span>
                        </label>
                        <InputTextarea id="description" name="description" placeholder={t('Enter Description')} value={formik?.values?.description} maxLength={200} onChange={formik.handleChange} disabled={viewable} />
                        {formik.touched.description && formik.errors.description && <small className="p-error">{formik.errors.description}</small>}
                    </div>
                </div>
            </div>

            <div className="flex justify-content-center mt-4 gap-2">
                {!viewable && (
                    <Button label={editable ? t('Update') : t('Save')} icon={editable ? 'pi pi-pencil' : 'pi pi-save'} type="submit" disabled={loadingBtn} className="p-button-success p-button-sm" style={{ minWidth: 90, maxWidth: 120, width: 'auto' }} />
                )}
                <Button label={t('Cancel')} icon="pi pi-times" type="button" className="p-button-secondary p-button-sm" style={{ minWidth: 90, maxWidth: 120, width: 'auto' }} onClick={() => onClose()} />
            </div>
        </form>
    );
};

export default AddEditPartsInventory;
